import { ColumnMapping } from '../../../Clients/ImportClients/autoMap';

const KEY_PREFIX = 'appointmint_appointments_import:';
const TTL_MS = 14 * 24 * 60 * 60 * 1000; // 2 weeks

type StoredMapping = Record<string, string[]>;

interface StoredEntry {
    savedAt: number;
    mapping: StoredMapping;
}

function hashHeaders(headers: string[]): string {
    const str = headers.join('\x00');
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = (((hash << 5) + hash) ^ str.charCodeAt(i)) >>> 0;
    }
    return hash.toString(36);
}

function storageKey(headers: string[]): string {
    return `${KEY_PREFIX}${hashHeaders(headers)}`;
}

function pruneExpired() {
    try {
        const now = Date.now();
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (!key?.startsWith(KEY_PREFIX)) continue;
            try {
                const entry: StoredEntry = JSON.parse(localStorage.getItem(key) ?? '');
                if (now - entry.savedAt > TTL_MS) localStorage.removeItem(key);
            } catch {
                localStorage.removeItem(key);
            }
        }
    } catch { /* ignore */ }
}

export function saveAppointmentMapping(fieldMap: Record<string, number[]>, headers: string[]) {
    const stored: StoredMapping = {};
    for (const [fieldId, indices] of Object.entries(fieldMap)) {
        for (const i of indices) {
            const header = headers[i];
            if (!header) continue;
            if (!stored[header]) stored[header] = [];
            if (!stored[header].includes(fieldId)) stored[header].push(fieldId);
        }
    }
    try {
        pruneExpired();
        const entry: StoredEntry = { savedAt: Date.now(), mapping: stored };
        localStorage.setItem(storageKey(headers), JSON.stringify(entry));
    } catch { /* ignore quota errors */ }
}

export function applyStoredAppointmentMapping(mapping: ColumnMapping, headers: string[]): ColumnMapping {
    try {
        const raw = localStorage.getItem(storageKey(headers));
        if (!raw) return mapping;
        const entry: StoredEntry = JSON.parse(raw);

        if (Date.now() - entry.savedAt > TTL_MS) {
            localStorage.removeItem(storageKey(headers));
            return mapping;
        }

        const stored = entry.mapping;
        const fieldMap: Record<string, number[]> = {};
        for (const [k, v] of Object.entries(mapping.fieldMap)) fieldMap[k] = [...v];

        const stillUnmapped: number[] = [];
        for (const colIndex of mapping.unmappedCols) {
            const header = headers[colIndex];
            const savedFieldIds = header ? (stored[header] ?? []) : [];
            if (savedFieldIds.length > 0) {
                for (const fieldId of savedFieldIds) {
                    if (!fieldMap[fieldId]) fieldMap[fieldId] = [];
                    fieldMap[fieldId].push(colIndex);
                }
            } else {
                stillUnmapped.push(colIndex);
            }
        }

        return { fieldMap, unmappedCols: stillUnmapped };
    } catch {
        return mapping;
    }
}
